import React, { useContext, useEffect, useMemo, useState } from "react";
import { useMount, usePrevious, useUnmount } from "react-use";
import { Undo } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Divider, Paper, Typography } from "@mui/material";
import combine from "@turf/combine";
import { Feature, FeatureCollection, Geometry } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";

import { LoadingButton } from "ui";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api";
import DrawHandler from "fond/draw/DrawHandler";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { ToolbarConfig } from "fond/map/Toolbar";
import { EditMode, setEditMode } from "fond/project/redux";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { ISGLayerId, isImportInProgress } from "fond/types";
import { removeDrawingProperties } from "fond/utils/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";

interface LayerDrawProps {
  versionId: string;
  layerId: ISGLayerId;
  displayName: string;
  drawingConfig: ToolbarConfig;
  filterFeatures: (feature: Feature<Geometry>) => boolean;
  combineToMultiPolygon?: boolean;
}

const LayerDraw: React.FC<LayerDrawProps> = ({ versionId, layerId, displayName, drawingConfig, filterFeatures, combineToMultiPolygon = false }) => {
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const { drawControl, setDrawMode } = useContext(MapContext);
  const layer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId }));
  const { data, isLoading } = useGetFeaturesQuery({ layerId: layer?.ID || "" }, { skip: !layer?.ID });
  const importState = useAppSelector((state) => state.imports[versionId]?.[layerId]);
  const uploading = isImportInProgress(importState?.status);
  const prevState = usePrevious(importState?.status);
  const { goBack } = useStackedNavigationContext();
  const initialFeatures = useMemo(() => data || [], [data]);
  const sources: FeatureIdentifier[] = useMemo(() => [{ source: "project-layers", sourceLayer: layerId }], [layerId]);

  useEffect(() => {
    if (prevState !== "COMPLETE" && importState?.status === "COMPLETE") {
      goBack();
    }
  }, [goBack, importState?.status, prevState]);

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  useUnmount(() => {
    setDrawMode("no_feature");
    dispatch(setEditMode(EditMode.none));
  });

  const handleOnCancel = () => {
    goBack();
  };

  const handleOnClick = () => {
    drawControl.current?.changeMode("simple_select");
    const features = drawControl.current?.getAll().features.filter(filterFeatures) || [];
    let collection = features.length > 0 ? featureCollection(features) : null;

    // Combine all Polygons into a single MultiPolygon if requested (for boundary layer)
    if (combineToMultiPolygon && collection) {
      collection = combine(collection) as ReturnType<typeof featureCollection>;
    }

    if (collection && collection.features.length > 0) {
      setIsSaving(true);
      setDrawMode("static");
      dispatch(setEditMode(EditMode.none));
      const cleanedCollection = removeDrawingProperties(collection as FeatureCollection);
      const file = new File([JSON.stringify(cleanedCollection)], "geojson.json", {
        type: "text/plain",
      });

      dispatch(addImport({ versionId, layerKey: layerId, displayName, files: [file] }));
    }
  };

  const handleOnReset = () => {
    drawControl.current?.deleteAll();
    drawControl.current?.set(featureCollection(initialFeatures || []));
  };

  if (isLoading) return <BlockSpinner />;

  const singularName = displayName.toLowerCase().replace(/s$/, "");
  const pluralName = displayName.toLowerCase();

  return (
    <>
      <Box data-testid="draw-panel" padding={1}>
        <Alert severity="info" data-testid="layer-alert">
          <AlertTitle sx={{ mb: 0 }}>Draw {pluralName}</AlertTitle>
        </Alert>
        {isSaving ? (
          <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
            <LinearProgressWithLabel color="primary" variant="indeterminate" title={`Generating ${singularName} data`} subtext="Processing..." />
          </Paper>
        ) : (
          <>
            <Divider sx={{ mt: 3 }} />
            <Button fullWidth sx={{ my: 0.5, justifyContent: "left" }} startIcon={<Undo sx={{ height: 20 }} />} onClick={handleOnReset}>
              <Typography color={(theme) => theme.palette.common.black} fontWeight={500} fontSize={13}>
                Reset
              </Typography>
            </Button>
            <Divider />
            <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
              <Button color="primary" size="small" onClick={handleOnCancel}>
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                onClick={handleOnClick}
                sx={{ ml: 1, px: 2 }}
                data-testid="finish-button"
                loading={isSaving}
              >
                Finish
              </LoadingButton>
            </Box>
          </>
        )}
      </Box>

      <DrawHandler initialFeatures={initialFeatures} config={drawingConfig} sources={sources} />

      {uploading && (
        <MapOverlay>
          <Paper sx={{ py: 1, px: 3, textAlign: "center", width: 200 }}>
            <BlockSpinner spinnerProps={{ size: 20 }} />
            <Typography component="p" variant="caption" pt={1}>
              Saving changes...
            </Typography>
          </Paper>
        </MapOverlay>
      )}
    </>
  );
};

export default LayerDraw;
